import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, Box, IconButton, Typography, Button, Grow, useMediaQuery, useTheme, alpha } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import {
  Close as CloseIcon,
  ChevronLeft,
  ChevronRight,
  ArrowForwardRounded as ArrowIcon,
  OpenInNewRounded as OpenIcon,
  LocalOfferRounded as OfferIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState, useAppDispatch } from "../../redux/store";
import { setPopupRead } from "../../redux/slices/authSlice";
import {
  getRetailerAdvertisementPopup,
  getAdvertisementPopupReadStatus,
  markAdvertisementPopupRead,
  trackAdvertisementPopupClick,
  RetailerAdvertisementPopup as PopupData,
} from "../../redux/apis/retailer/advertisementPopupApis";
import { POPUP_IMAGE_SPECS } from "./advertisementPopupSpecs";

// Retailer-only advertisement pop-up.
//
// Flow: whenever a retailer session is present (fresh login or a persisted
// token after a page load) we ask /retailer/advertisementPopup/readStatus.
// It is false on every page load while the live pop-up has "show one time" off,
// and for a "show one time" pop-up until the retailer has closed it. When it is
// false we fetch the live pop-up and show it once for this page load. Only an explicit click
// inside the dialog (image, action button, "Not now", cross) marks it read;
// the backdrop and Escape do nothing. Tapping also records a click (platform
// "popup", deviceType "web") and, for master-search pop-ups, carries popupId
// to the order page so add-to-cart calls can be attributed to it.
//
// Responsive image choice: desktop/tablet use the web set (16:9 frame); phone
// widths (< 600px) switch to the mobile set (4:5 frame) when one exists. Each
// set renders inside a fixed-ratio frame with object-fit: cover, so images
// uploaded at the recommended size fill the frame exactly on every screen.

const AUTO_ADVANCE_MS = 4500;

const toStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map((item) => String(item));
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map((item) => String(item));
    } catch {
      return value.split(",").map((s) => s.trim()).filter(Boolean);
    }
  }
  return [];
};

const ZoomTransition = React.forwardRef(function ZoomTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Grow ref={ref} {...props} timeout={{ enter: 320, exit: 200 }} />;
});

const RetailerAdvertisementPopup: React.FC = () => {
  const { role, token, popupRead } = useSelector((state: RootState) => state.auth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  const [popup, setPopup] = useState<PopupData | null>(null);
  const [open, setOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const statusCheckedForToken = useRef<string | null>(null);
  const fetchedForToken = useRef<string | null>(null);

  const isRetailerSession = role === "retailer" && !!token;

  // 1. Refresh the read flag for this session (covers already-signed-in users
  //    whose persisted popupRead predates the nightly reset).
  useEffect(() => {
    if (!isRetailerSession || !token) return;
    if (statusCheckedForToken.current === token) return;
    statusCheckedForToken.current = token;

    let cancelled = false;
    (async () => {
      try {
        const response = await getAdvertisementPopupReadStatus();
        const read = response?.data?.data?.popupRead;
        if (!cancelled && typeof read === "boolean") dispatch(setPopupRead(read));
      } catch (error) {
        console.error("Failed to read advertisement pop-up status:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isRetailerSession, token, dispatch]);

  // 2. When the flag says unread, fetch the live pop-up once per session.
  const shouldFetch = isRetailerSession && popupRead === false;

  useEffect(() => {
    if (!shouldFetch || !token) return;
    if (fetchedForToken.current === token) return;
    fetchedForToken.current = token;

    let cancelled = false;
    (async () => {
      try {
        const response = await getRetailerAdvertisementPopup();
        const data: any = response?.data?.data ?? null;
        if (cancelled || !data) return;
        const normalized: PopupData = {
          ...data,
          webImages: toStringArray(data.webImages),
          mobileImages: toStringArray(data.mobileImages),
          inventors: toStringArray(data.inventors),
          redirectType: data.redirectType || "none",
        };
        if (!normalized.webImages.length && !normalized.mobileImages.length && !normalized.title) return;
        setPopup(normalized);
        setImageIndex(0);
        setOpen(true);
      } catch (error) {
        console.error("Failed to load advertisement pop-up:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [shouldFetch, token]);

  const useMobileSet = popup ? (isPhone ? popup.mobileImages.length > 0 : popup.webImages.length === 0) : false;
  const images = popup ? (useMobileSet ? popup.mobileImages : popup.webImages) : [];
  const imageCount = images.length;

  // 3. Auto-advance the carousel while open (pauses on hover / touch).
  useEffect(() => {
    if (!open || paused || imageCount < 2) return;
    const id = window.setInterval(() => {
      setImageIndex((index) => (index + 1) % imageCount);
    }, AUTO_ADVANCE_MS);
    return () => window.clearInterval(id);
  }, [open, paused, imageCount]);

  const markRead = useCallback(() => {
    dispatch(setPopupRead(true));
    markAdvertisementPopupRead().catch((error) => {
      console.error("Failed to mark advertisement pop-up as read:", error);
    });
  }, [dispatch]);

  const handleClose = () => {
    setOpen(false);
    markRead();
  };

  const handleTap = () => {
    if (!popup) return;
    setOpen(false);
    markRead();
    trackAdvertisementPopupClick(popup.id, "web").catch((error) => {
      console.error("Failed to track advertisement pop-up click:", error);
    });
    if (popup.redirectType === "url" && popup.redirectUrl) {
      window.open(popup.redirectUrl, "_blank", "noopener,noreferrer");
    } else if (popup.redirectType === "masterSearch" && popup.inventors.length > 0) {
      const params = new URLSearchParams({
        masterSearch: popup.inventors.join(","),
        popupId: String(popup.id),
      });
      navigate(`/retailer/order?${params.toString()}`);
    }
  };

  if (!popup) return null;

  const frameRatio = useMobileSet ? POPUP_IMAGE_SPECS.mobile.ratio : POPUP_IMAGE_SPECS.web.ratio;
  const hasRedirect =
    (popup.redirectType === "url" && !!popup.redirectUrl) ||
    (popup.redirectType === "masterSearch" && popup.inventors.length > 0);
  const safeIndex = Math.min(imageIndex, Math.max(imageCount - 1, 0));
  const isDark = theme.palette.mode === "dark";

  const closeLabel = t("common.close", { defaultValue: "Close" });
  const laterLabel = t("advertisementPopup.notNow", { defaultValue: "Not now" });
  const captionLabel = t("advertisementPopup.caption", { defaultValue: "Today's offer" });
  const ctaLabel =
    popup.redirectType === "url"
      ? t("advertisementPopup.openLink", { defaultValue: "Open link" })
      : popup.redirectType === "masterSearch"
        ? t("advertisementPopup.viewProducts", { defaultValue: "View products" })
        : closeLabel;
  const CtaIcon = popup.redirectType === "url" ? OpenIcon : ArrowIcon;

  // Frosted circular controls that sit on top of the image.
  const glassSx = {
    width: 36,
    height: 36,
    color: "#111827",
    bgcolor: "rgba(255,255,255,0.86)",
    backdropFilter: "blur(6px)",
    boxShadow: "0 2px 10px rgba(0,0,0,0.18)",
    transition: "transform 0.15s ease, background-color 0.15s ease",
    "&:hover": { bgcolor: "#fff", transform: "scale(1.06)" },
  };

  const pillButtonSx = {
    borderRadius: 999,
    textTransform: "none" as const,
    fontWeight: 600,
    fontSize: 15,
    px: 3,
    py: 1.1,
    boxShadow: "none",
    width: { xs: "100%", sm: "auto" },
  };

  return (
    // No onClose: backdrop clicks and Escape do not dismiss the pop-up, so the
    // mark-as-read call only happens on an explicit click inside it.
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth={false}
      TransitionComponent={ZoomTransition}
      slotProps={{
        backdrop: {
          sx: { bgcolor: "rgba(10, 16, 28, 0.62)", backdropFilter: "blur(5px)" },
        },
      }}
      PaperProps={{
        sx: {
          width: "100%",
          // Portrait (4:5) frames need a narrower card so the image is not taller than the screen.
          maxWidth: useMobileSet ? 400 : 640,
          m: { xs: 1.5, sm: 3 },
          borderRadius: { xs: 4, sm: 5 },
          overflow: "hidden",
          bgcolor: "background.paper",
          boxShadow: "0 30px 80px rgba(0,0,0,0.45)",
        },
      }}
    >
      {/* ---------------- Image / carousel ---------------- */}
      {imageCount > 0 ? (
        <Box
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={() => setPaused(true)}
          onTouchEnd={() => setPaused(false)}
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: frameRatio,
            maxHeight: "68vh",
            bgcolor: isDark ? "#0b0f19" : "#e9edf3",
            overflow: "hidden",
          }}
        >
          {images.map((src, index) => (
            <Box
              key={`${src}-${index}`}
              component="img"
              src={src}
              alt={index === safeIndex ? popup.title || "" : ""}
              draggable={false}
              onClick={hasRedirect ? handleTap : undefined}
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                cursor: hasRedirect ? "pointer" : "default",
                opacity: index === safeIndex ? 1 : 0,
                transform: index === safeIndex ? "scale(1)" : "scale(1.03)",
                transition: "opacity 0.5s ease, transform 0.9s ease",
                pointerEvents: index === safeIndex ? "auto" : "none",
              }}
            />
          ))}

          <IconButton onClick={handleClose} aria-label={closeLabel} sx={{ ...glassSx, position: "absolute", top: 12, right: 12, zIndex: 3 }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>

          {imageCount > 1 && (
            <>
              <IconButton
                aria-label="Previous image"
                onClick={(e) => {
                  e.stopPropagation();
                  setImageIndex((safeIndex - 1 + imageCount) % imageCount);
                }}
                sx={{ ...glassSx, position: "absolute", top: "50%", left: 12, transform: "translateY(-50%)", "&:hover": { bgcolor: "#fff", transform: "translateY(-50%) scale(1.06)" } }}
              >
                <ChevronLeft />
              </IconButton>
              <IconButton
                aria-label="Next image"
                onClick={(e) => {
                  e.stopPropagation();
                  setImageIndex((safeIndex + 1) % imageCount);
                }}
                sx={{ ...glassSx, position: "absolute", top: "50%", right: 12, transform: "translateY(-50%)", "&:hover": { bgcolor: "#fff", transform: "translateY(-50%) scale(1.06)" } }}
              >
                <ChevronRight />
              </IconButton>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 14,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 999,
                  bgcolor: "rgba(17,24,39,0.45)",
                  backdropFilter: "blur(6px)",
                  zIndex: 2,
                }}
              >
                {images.map((_, index) => (
                  <Box
                    key={index}
                    role="button"
                    aria-label={`Image ${index + 1}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setImageIndex(index);
                    }}
                    sx={{
                      width: index === safeIndex ? 20 : 7,
                      height: 7,
                      borderRadius: 4,
                      bgcolor: index === safeIndex ? "#fff" : "rgba(255,255,255,0.55)",
                      cursor: "pointer",
                      transition: "width 0.25s ease, background-color 0.25s ease",
                    }}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>
      ) : (
        // Text-only pop-up: a branded header band instead of an image.
        <Box
          sx={{
            position: "relative",
            px: 3,
            pt: 4,
            pb: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: theme.palette.primary.contrastText,
          }}
        >
          <IconButton onClick={handleClose} aria-label={closeLabel} sx={{ ...glassSx, position: "absolute", top: 12, right: 12 }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <OfferIcon sx={{ fontSize: 40, opacity: 0.9 }} />
        </Box>
      )}

      {/* ---------------- Content panel ---------------- */}
      <Box sx={{ px: { xs: 2.5, sm: 3.5 }, pt: { xs: 2, sm: 2.5 }, pb: { xs: 2.5, sm: 3 }, display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "primary.main" }}>
          <OfferIcon sx={{ fontSize: 16 }} />
          <Typography fontSize={12} fontWeight={700} letterSpacing={0.8} sx={{ textTransform: "uppercase" }}>
            {captionLabel}
          </Typography>
        </Box>

        {popup.title && (
          <Typography
            component="h2"
            fontSize={{ xs: 21, sm: 24 }}
            fontWeight={700}
            color="text.primary"
            sx={{ lineHeight: 1.2, letterSpacing: -0.2 }}
          >
            {popup.title}
          </Typography>
        )}

        {popup.description && (
          <Typography fontSize={{ xs: 14, sm: 15 }} color="text.secondary" sx={{ whiteSpace: "pre-line", lineHeight: 1.55 }}>
            {popup.description}
          </Typography>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 1.5 },
            mt: { xs: 1.5, sm: 2 },
            flexDirection: { xs: "column-reverse", sm: "row" },
            justifyContent: { sm: "flex-end" },
          }}
        >
          {hasRedirect && (
            <Button
              variant="text"
              color="inherit"
              onClick={handleClose}
              sx={{ ...pillButtonSx, color: "text.secondary", "&:hover": { bgcolor: "action.hover" } }}
            >
              {laterLabel}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            disableElevation
            onClick={hasRedirect ? handleTap : handleClose}
            endIcon={hasRedirect ? <CtaIcon /> : undefined}
            sx={{
              ...pillButtonSx,
              minWidth: 160,
              color: "#fff",
              boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, 0.35)}`,
              "&:hover": { boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.45)}` },
            }}
          >
            {ctaLabel}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default RetailerAdvertisementPopup;
